// Fix 3 (hotfix pérdida de datos 31/07/2026): con muchos registros sin subir
// un pill discreto en el header no alcanza — se necesita un aviso persistente
// en toda la app para que el usuario sepa que debe dejarla abierta con señal.
// Tanda 3 (avisos proactivos +24h/+72h): además de la cantidad, se avisa por
// TIEMPO — un puñado de registros atascados varios días (ej. un error
// silencioso que nadie notó) es tan grave como una cola de miles.
// Fix I (2026-09-17): el aviso ahora dice la VERDAD. Antes decía siempre
// "conectate a internet", incluso cuando había señal y el problema era la
// sesión vencida. Ahora se distinguen tres casos: sin conexión / sesión
// vencida (lo cubre SessionExpiredBanner) / con sesión válida pero la cola no
// baja (link al detalle en Ajustes → Sincronización).
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CloudUpload, AlertTriangle } from "lucide-react";
import { onSyncState, getPendingCount, getOldestPendingCreatedAt, isSessionExpired } from "@/lib/sync";
import { useOnline } from "@/lib/useOnline";

const COUNT_THRESHOLD = 100;
const HOURS_WARN = 24;
const HOURS_CRITICAL = 72;

export function PendingSyncBanner() {
  const navigate = useNavigate();
  const online = useOnline();
  const [pending, setPending] = useState(0);
  const [oldestHours, setOldestHours] = useState(null);
  const [expired, setExpired] = useState(isSessionExpired());

  useEffect(() => {
    const refresh = async () => {
      const [count, oldest] = await Promise.all([getPendingCount(), getOldestPendingCreatedAt()]);
      setPending(count);
      setOldestHours(oldest ? (Date.now() - new Date(oldest).getTime()) / 3600000 : null);
      setExpired(isSessionExpired());
    };
    refresh();
    const off = onSyncState(refresh);
    return off;
  }, []);

  if (pending === 0) return null;
  // Sesión vencida: el cartel de renovación (SessionExpiredBanner) ya explica
  // el problema real y ofrece la solución — no se duplica el aviso.
  if (expired) return null;
  const critical = oldestHours !== null && oldestHours >= HOURS_CRITICAL;
  const warn = oldestHours !== null && oldestHours >= HOURS_WARN;
  if (!critical && !warn && pending <= COUNT_THRESHOLD) return null;

  const days = oldestHours !== null ? Math.floor(oldestHours / 24) : null;
  const goToDetail = () => navigate("/ajustes/sync");

  if (critical) {
    return (
      <div data-testid="pending-sync-banner" data-severity="critical" data-cause={online ? "upload_problem" : "offline"} role="alert"
        className="slide-down border-b border-destructive/50 bg-destructive/10 text-destructive">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center gap-2 text-xs font-medium">
          <AlertTriangle size={14} />
          <span className="text-foreground/90">
            Hace más de <span className="font-semibold">{days} día(s)</span> que tenés{" "}
            <span className="font-semibold" data-testid="pending-sync-banner-count">{pending}</span> registro(s) sin subir.
            {online ? (
              <button data-testid="pending-sync-banner-detail-link" onClick={goToDetail}
                className="font-normal underline underline-offset-2 ml-1">
                Hay un problema al subir. Tocá aquí para ver el detalle.
              </button>
            ) : (
              <span className="font-normal"> Conectate a internet lo antes posible — hay riesgo de perder estos datos si se pierde el dispositivo.</span>
            )}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="pending-sync-banner"
      data-severity={warn ? "warn" : "info"}
      data-cause={online ? "upload_problem" : "offline"}
      role="status"
      className="slide-down border-b border-[hsl(var(--brand-ochre)/0.4)] bg-[hsl(var(--brand-ochre)/0.14)] text-[hsl(var(--brand-ochre))]"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center gap-2 text-xs font-medium">
        <CloudUpload size={14} />
        <span className="text-foreground/90">
          Tenés <span className="font-semibold" data-testid="pending-sync-banner-count">{pending}</span> registros sin subir.
          {warn && online ? (
            <button data-testid="pending-sync-banner-detail-link" onClick={goToDetail}
              className="font-normal underline underline-offset-2 ml-1">
              Hay un problema al subir. Tocá aquí para ver el detalle.
            </button>
          ) : warn ? (
            <span className="font-normal"> Hace más de 24hs que están pendientes — conectate a internet lo antes posible.</span>
          ) : (
            <span className="text-muted-foreground font-normal"> Dejá la app abierta con señal hasta que termine.</span>
          )}
        </span>
      </div>
    </div>
  );
}
